// ═══════════════════════════════════════════════════════════════
// TenantStatusPanel.jsx — Tenant status display + cancel/archive UI
// Extracted from SupportDashboard.jsx for billing module reuse.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useCallback } from 'react';
import { CheckCircle, AlertCircle, XCircle, Archive, AlertTriangle } from 'lucide-react';
import { getTenantDaysRemaining } from './BillingEnforcement.js';
import { DEFAULT_CONFIG } from './BillingConfig.js';

// ── Status metadata ──────────────────────────────────────────

const STATUS_META = {
  active:    { label: 'Active',    icon: CheckCircle,  color: 'text-emerald-700' },
  trial:     { label: 'Trial',     icon: AlertCircle,  color: 'text-blue-600'    },
  cancelled: { label: 'Cancelled', icon: XCircle,      color: 'text-red-700'     },
  archived:  { label: 'Archived',  icon: Archive,      color: 'text-stone-500'   },
  pending:   { label: 'Pending',   icon: AlertCircle,  color: 'text-amber-700'   },
};

// System tenants that cannot be cancelled
const SYSTEM_TENANTS = ['tenant-hci', 'tenant-public'];

/**
 * Tenant status badge.
 * <TenantStatusBadge tenant={tenant} />
 */
function TenantStatusBadge({ tenant }) {
  const meta = STATUS_META[tenant?.status] || STATUS_META.active;
  const StatusIcon = meta.icon;
  const daysRemaining = getTenantDaysRemaining(tenant);

  return (
    <span className={`flex items-center gap-1 text-xs font-medium ${meta.color}`}>
      <StatusIcon className="w-3.5 h-3.5" />
      {meta.label}
      {tenant?.status === 'cancelled' && daysRemaining != null && (
        <span className="ml-1 text-stone-500">({daysRemaining}d remaining)</span>
      )}
    </span>
  );
}

/**
 * Billing contact display.
 * <BillingContact tenant={tenant} />
 */
function BillingContact({ tenant }) {
  if (!tenant?.billing?.contactEmail) return null;
  return (
    <div className="mt-4 pt-4 border-t border-stone-100 text-xs text-stone-500">
      Billing contact: <span className="text-stone-700">{tenant.billing.contactName || '—'}</span>
      {' '}·{' '}
      <a href={`mailto:${tenant.billing.contactEmail}`} className="text-amber-700 hover:underline">
        {tenant.billing.contactEmail}
      </a>
    </div>
  );
}

/**
 * Cancel tenant button with confirmation dialog.
 * Only shown for non-system tenants with active/trial status.
 * <CancelTenantButton tenant={tenant} stripeAdapter={stripeAdapter} user={user} onCancelled={fn} />
 */
function CancelTenantButton({ tenant, stripeAdapter, user, onCancelled, onError }) {
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Don't show for system tenants or already cancelled/archived
  if (!tenant || SYSTEM_TENANTS.includes(tenant.id)) return null;
  if (tenant.status === 'cancelled' || tenant.status === 'archived') return null;

  const handleCancel = useCallback(async () => {
    setCancelling(true);
    try {
      const result = await stripeAdapter.cancelTenant({
        tenantId: tenant.id,
        cancelledBy: user?.id || 'unknown',
      });
      if (result.success) {
        setConfirming(false);
        onCancelled?.(result);
      } else {
        onError?.(result.error || 'Failed to cancel tenant.');
      }
    } catch (e) {
      console.error('Cancel tenant error:', e);
      onError?.('Could not cancel tenant. Please try again.');
    } finally {
      setCancelling(false);
    }
  }, [tenant.id, stripeAdapter, user?.id, onCancelled, onError]);

  if (confirming) {
    return (
      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-start gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-red-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            <p className="font-medium mb-1">Cancel "{tenant.name}"?</p>
            <p>All users will lose edit access immediately. Data will be retained for {DEFAULT_CONFIG.gracePeriodDays} days before archival.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleCancel}
            disabled={cancelling}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white text-xs font-medium rounded-lg transition-colors"
          >
            {cancelling ? 'Cancelling...' : 'Yes, Cancel Tenant'}
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={cancelling}
            className="px-3 py-1.5 bg-stone-200 hover:bg-stone-300 text-stone-700 text-xs font-medium rounded-lg transition-colors"
          >
            Keep Active
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => setConfirming(true)}
      className="mt-2 px-3 py-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
    >
      Cancel Tenant
    </button>
  );
}

export { STATUS_META, TenantStatusBadge, BillingContact, CancelTenantButton, SYSTEM_TENANTS };
